import { type FC, useEffect, useState } from 'react'
import { LoadingOverlay, toast } from 'react-common'
import { Outlet } from 'react-router-dom'
import { AxiosError } from 'axios'
import { useSetAcronisBodyClass } from '@/features/layout/hooks/useSetAcronisBodyClass'
import { useDocTitle } from '@/features/wa/title/useDocTitle'
import { useCoursePageParams } from '@/hooks/useCoursePageParams'
import { useIsCatalogCourse } from '@/hooks/useIsCatalogCourse'
import { useMemoTranslation } from '@/hooks/useMemoTranslation'
import { useGetObjectiveAccessByIdQuery } from '@/store/api/aggregatesApi'
import { objectiveWorkflowApi } from '@/store/api/objectiveWorkflowApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  fetchCurrentFromObjectiveAsync,
  reset as resetAggregate,
  resetLtiSourceAction,
} from '@/store/slices/aggregateSlice'

type CourseParentProps = {
  onError: VoidFunction
}

export const CourseParent: FC<CourseParentProps> = ({ onError }) => {
  useSetAcronisBodyClass('course-bg')

  const { t } = useMemoTranslation()
  const dispatch = useAppDispatch()
  const isCatalog = useIsCatalogCourse()
  const { objectiveId = '' } = useCoursePageParams()

  const current = useAppSelector((state) => state.aggregate.current)

  const [loaded, setLoaded] = useState(false)

  const { data: objectiveAccess, isLoading } = useGetObjectiveAccessByIdQuery(objectiveId, {
    skip: !objectiveId,
  })

  useEffect(() => {
    if (isLoading) {
      return
    }

    if (!objectiveId) {
      setLoaded(true)
      return
    }

    const loadCourse = async () => {
      try {
        /* in case of 204, we should show 404 */
        if (!objectiveAccess) {
          onError()
          return
        }

        if (!isCatalog) {
          await dispatch(fetchCurrentFromObjectiveAsync(objectiveId))
        }
      } catch (error) {
        if (!(error instanceof AxiosError)) {
          toast.critical(t('_errors.unknown'))
          return
        }

        if ([400, 401, 403, 404].includes(error.status || (error.response?.status as number))) {
          onError()
        }
      } finally {
        setLoaded(true)
        dispatch(resetLtiSourceAction())
      }
    }

    loadCourse()
  }, [t, dispatch, objectiveAccess, onError, objectiveId, isCatalog, isLoading])

  useDocTitle(current?.objective?.name || objectiveAccess?.objective?.name || '')

  useEffect(() => {
    return () => {
      dispatch(objectiveWorkflowApi.util.resetApiState())
      dispatch(resetAggregate())
    }
  }, [dispatch])

  if (!loaded) return <LoadingOverlay />

  return <Outlet />
}
